import { useState } from 'react'
import { useOutletContext } from 'react-router'
import { PageFooter } from '@/components/commons/page-footer'
import { PageHero } from '@/components/commons/page-hero'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { ArrowRightIcon } from '@/components/ui/icons'
import { Separator } from '@/components/ui/separator'
import { InitialQaImagesPool } from '@/features/photoset/components/initial-qa-images-pool'
import {
	PhotosetStepGridContainer,
	PhotosetStepWrapper
} from '@/features/photoset/components/photoset-step-atoms'
import { RejectPhotosetModal } from '@/features/photoset/components/reject-photoset-modal'
import { ShootListMetadataSidebar } from '@/features/photoset/components/shoot-list-metadata-sidebar'
import { StepHeroControls } from '@/features/photoset/components/step-hero-controls'
import { useGetFlags } from '@/features/photoset/queries/use-get-flags'
import { useGetReasonsDecline } from '@/features/photoset/queries/use-get-reasons-decline'
import { useUpdateStepToAllocation } from '@/features/photoset/queries/use-update-step-to-allocation'
import { usePhotosetStore } from '@/features/photoset/store/store'
import type { PhotosetURLParams } from '@/features/photoset/types'
import { errorHandler } from '@/helpers/utils'
import { QUERY_KEYS, queryClient } from '@/query-client'
import { useTranslation } from '@/translations/i18n'

export const PhotosetInitialQaStep = () => {
	const [hasSidebar, setHasSidebar] = useState(true)
	const { t } = useTranslation()
	const { urlParams } = useOutletContext<{ urlParams: PhotosetURLParams }>()
	const [rejectModal, showRejectModal] = useState(false)

	//queries
	const reasonsDeclineQuery = useGetReasonsDecline()

	//store actions
	const unselectAll = usePhotosetStore((state) => state.unselectAll)

	//mutations
	const updateStepToAllocationMutation = useUpdateStepToAllocation({
		onError: (error) => errorHandler(error),
		onSuccess: () => {
			unselectAll()
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.PHOTOSET]
			})
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.IMAGES]
			})
		}
	})

	// get the flags list
	useGetFlags(
		{
			params: {
				types: ['metadata', 'visual_metadata', 'visual'],
				response_type: 'extended'
			}
		},
		{ staleTime: Number.POSITIVE_INFINITY }
	)

	return (
		<>
			<PageHero
				endSlot={<StepHeroControls />}
				subtitle={t('photoset:initial_qa.subtitle')}
				title={t('photoset:initial_qa.title')}
			/>

			<PhotosetStepWrapper>
				<PhotosetStepGridContainer>
					<InitialQaImagesPool
						hasSidebar={hasSidebar}
						showSidebar={() => setHasSidebar(true)}
					/>
					{hasSidebar && (
						<>
							<Separator orientation='vertical' />
							<ShootListMetadataSidebar
								hideSidebar={() => setHasSidebar(false)}
							/>
						</>
					)}
				</PhotosetStepGridContainer>
				<PageFooter
					centerSlot={
						<p>{t('photoset:initial_qa.footer_step_label')}</p>
					}
					endSlot={
						<ButtonLegacy
							disabled={updateStepToAllocationMutation.isPending}
							onClick={() =>
								updateStepToAllocationMutation.mutate(
									urlParams.id
								)
							}
							type='button'
						>
							{t('photoset:allocation.title')}
							<ArrowRightIcon />
						</ButtonLegacy>
					}
					startSlot={
						<ButtonLegacy
							disabled={
								reasonsDeclineQuery.isFetching ||
								updateStepToAllocationMutation.isPending
							}
							onClick={() => showRejectModal(true)}
							type='button'
							variant='tertiary'
						>
							{t('common:needs_reupload')}
						</ButtonLegacy>
					}
				/>

				{rejectModal && reasonsDeclineQuery.data && (
					<RejectPhotosetModal
						close={() => showRejectModal(false)}
						reasons={reasonsDeclineQuery.data}
					/>
				)}
			</PhotosetStepWrapper>
		</>
	)
}

PhotosetInitialQaStep.displayName = 'PhotosetInitialQaStep'
